// src/pages/RegistroFatturazionePage.js
// «Richieste di fatturazione» (ottobre 2026). Ogni PM vede solo le richieste che ha fatto
// lui e può annullarle finché sono «Da fatturare»; titolare, admin e chi ha il permesso
// «da-fatturare» (di default Gestione aziendale e Amministrazione) le vedono tutte, divise
// per PM, e ne cambiano lo stato. La separazione è nelle regole Firestore (57), non solo qui.
// I totali della commessa (percentuale richiesta, in attesa, fatturato) si ricalcolano da
// utils/fatturazioneCommessa.js e finiscono anche sul Computo SAL.
import React, { useState, useEffect, useRef } from "react";
import { collection, query, where, onSnapshot, doc, getDoc, updateDoc, serverTimestamp, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import { useOrg } from "../contexts/OrgContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { messaggioErrore } from "../utils/erroriUtente";
import { toLocalDateStr } from "../utils/dateUtils";
import { statoVariante, importoVariante, vociStorico } from "../utils/varianti";
import { sincronizzaVariantiSuBudget } from "../utils/variantiSync";
import { FileText, Search } from "lucide-react";
import { confirm } from "../utils/confirm";
import { useVedeTutteFatturazioni } from "../hooks/useCanSee";
import { sincronizzaFatturazioneCommessa, sincronizzaTutteLeCommesse, annullaRichiestaFatturazione } from "../utils/fatturazioneCommessa";
import { avvisaRichiedente } from "../utils/avvisiFatturazione";

const STATI = {
  da_fatturare:  { label: "Da fatturare",  bg: "#fef3c7", col: "#92400e" },
  in_lavorazione:{ label: "In lavorazione", bg: "#dbeafe", col: "#1d4ed8" },
  fatturata:     { label: "Fatturata",      bg: "#d1fae5", col: "#065f46" },
  annullata:     { label: "Annullata",      bg: "#f3f4f6", col: "#6b7280" },
};

function formatEuro(n) {
  return n === null || n === undefined ? "Importo da verificare" : `€ ${new Intl.NumberFormat("it-IT",{minimumFractionDigits:2,maximumFractionDigits:2}).format(n)}`;
}

export default function RegistroFatturazionePage({ onNavigate, deepLink }) {
  const { effectiveOrgId } = useOrg();
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const [vista, setVista] = useState("richieste"); // "richieste" | "varianti"
  const [varianti, setVarianti] = useState([]);
  const [nomiCommesse, setNomiCommesse] = useState({});
  const [commesseCaricate, setCommesseCaricate] = useState(false);
  const [confermando, setConfermando] = useState(false);
  const [fatturando, setFatturando] = useState(null); // { id, fatturaRif, dataFattura }
  const [richieste, setRichieste] = useState([]);
  const [cerca, setCerca] = useState("");
  const [filtroStato, setFiltroStato] = useState("");
  const [filtroCommessa, setFiltroCommessa] = useState(deepLink?.projectId || "");
  const [filtroPm, setFiltroPm] = useState("");
  const [inFattura, setInFattura] = useState(null); // { id, fatturaRif, dataFattura } richiesta da segnare fatturata
  const vedeTutte = useVedeTutteFatturazioni(); // null = permessi in caricamento
  const mioUid = currentUser?.uid;

  // Se si arriva qui da "Relazioni della Commessa", precompila il filtro sulla commessa di origine
  useEffect(() => { if (deepLink?.projectId) setFiltroCommessa(deepLink.projectId); }, [deepLink?.projectId]);

  useEffect(() => {
    if (!effectiveOrgId || vedeTutte === null || !mioUid) return;
    const filtri = [where("organizationId", "==", effectiveOrgId)];
    if (!vedeTutte) filtri.push(where("richiedenteUid", "==", mioUid));
    return onSnapshot(query(collection(db, "richiesteFatturazione"), ...filtri), snap => {
      const l = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      l.sort((a,b) => (b.dataRichiesta||"").localeCompare(a.dataRichiesta||"") || (b.numero||"").localeCompare(a.numero||""));
      setRichieste(l);
    }, err => { console.warn("Richieste di fatturazione non lette:", err?.code || err); setRichieste([]); });
  }, [effectiveOrgId, vedeTutte, mioUid]);

  // Chi vede tutto, aprendo la pagina, riallinea i totali di ogni commessa (percentuale
  // richiesta, in attesa, fatturato) e il fatturato dei SAL — una volta per apertura.
  const riallineato = useRef(false);
  useEffect(() => {
    if (vedeTutte !== true || riallineato.current || !effectiveOrgId || richieste.length === 0) return;
    riallineato.current = true;
    sincronizzaTutteLeCommesse(richieste, effectiveOrgId);
  }, [vedeTutte, richieste, effectiveOrgId]);

  // Varianti e lavori extra valorizzati, da fatturare una per una (scheda Varianti della commessa)
  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(query(collection(db, "attivitaExtra"), where("orgId", "==", effectiveOrgId)), snap => {
      setVarianti(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.data || "").localeCompare(b.data || "")));
    });
  }, [effectiveOrgId]);
  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(query(collection(db, "projects"), where("organizationId", "==", effectiveOrgId)), snap => {
      const m = {};
      snap.docs.forEach(d => { const p = d.data(); m[d.id] = { name: p.name || "", codiceCommessa: p.codiceCommessa || "", numeroOrdine: p.numeroOrdine || "", clienteNome: p.clienteNome || "", responsabileUid: p.responsabileUid || "" }; });
      setNomiCommesse(m);
      setCommesseCaricate(true);
    });
  }, [effectiveOrgId]);
  // Solo varianti di commesse che esistono ancora (quelle di commesse eliminate non si fatturano)
  // Il PM vede le varianti delle commesse di cui è responsabile; chi vede tutto le vede tutte
  // (con il filtro per PM: le varianti del PM responsabile della commessa)
  const pmDellaVariante = v => nomiCommesse[v.projectId]?.responsabileUid || "";
  const variantiValide = (commesseCaricate ? varianti.filter(v => nomiCommesse[v.projectId]) : [])
    .filter(v => vedeTutte ? (!filtroPm || pmDellaVariante(v) === filtroPm) : pmDellaVariante(v) === mioUid);
  const variantiDaFatturare = variantiValide.filter(v => statoVariante(v).id === "valorizzata");
  const totaleDaFatturare = variantiDaFatturare.filter(v => !filtroCommessa || v.projectId === filtroCommessa)
    .reduce((s, v) => s + (importoVariante(v) || 0), 0);
  const variantiVisibili = variantiValide.filter(v => {
    const st = statoVariante(v).id;
    if (filtroStato === "fatturata" ? st !== "fatturata" : st !== "valorizzata") return false;
    if (filtroCommessa && v.projectId !== filtroCommessa) return false;
    if (!cerca) return true;
    const c = nomiCommesse[v.projectId] || {};
    const q = cerca.toLowerCase();
    return [c.numeroOrdine, v.titolo, v.projectName || c.name, v.codiceCommessa || c.codiceCommessa, v.clienteNome || c.clienteNome, v.fatturaRif].some(x => (x || "").toLowerCase().includes(q));
  });
  const confermaVarianteFatturata = async () => {
    if (confermando || !vedeTutte) return;
    const v = varianti.find(x => x.id === fatturando?.id);
    if (!v) return;
    const chi = currentUser?.displayName || currentUser?.email || "";
    const dopo = { stato: "Fatturata", fatturaRif: fatturando.fatturaRif.trim(), dataFattura: fatturando.dataFattura || "" };
    setConfermando(true);
    try {
      const fresca = await getDoc(doc(db, "attivitaExtra", v.id));
      if (!fresca.exists() || statoVariante(fresca.data()).id !== "valorizzata") {
        addToast("Nel frattempo la variante è cambiata (fatturata, modificata o eliminata da un collega)", "error");
        setFatturando(null); return;
      }
      await updateDoc(doc(db, "attivitaExtra", v.id), {
        ...dopo, fatturata: true, fatturataDaNome: chi, fatturataIl: serverTimestamp(),
        storico: arrayUnion(...vociStorico(v, dopo, chi, new Date().toISOString())), updatedAt: serverTimestamp(),
      });
      setFatturando(null);
      addToast("Variante segnata come fatturata", "success");
      sincronizzaVariantiSuBudget(v.projectId, effectiveOrgId).catch(err => console.warn("Riepilogo varianti non aggiornato:", err));
    } catch (err) { addToast(messaggioErrore(err, "segnare la fatturazione"), "error"); }
    finally { setConfermando(false); }
  };

  // Cambio di stato (solo chi vede tutte le richieste). «Fatturata» chiede numero e data
  // della fattura; ogni cambio ricalcola i totali della commessa e avvisa il PM.
  const cambiaStato = async (r, nuovo, extra = {}) => {
    if (!vedeTutte || nuovo === r.stato) return;
    if (nuovo === "fatturata" && !extra.fatturaRif) { setInFattura({ id: r.id, fatturaRif: "", dataFattura: toLocalDateStr(new Date()) }); return; }
    if (nuovo === "annullata" && !await confirm(`Annullare la richiesta ${r.numero || ""}? La percentuale torna disponibile sulla commessa e il PM riceve un avviso.`, { type: "danger" })) return;
    if (r.stato === "fatturata" && !await confirm(`La richiesta ${r.numero || ""} risulta già fatturata${r.fatturaRif ? ` (fattura ${r.fatturaRif})` : ""}. Cambiare comunque lo stato?`, { type: "danger" })) return;
    const chi = currentUser?.displayName || currentUser?.email || "";
    try {
      await updateDoc(doc(db, "richiesteFatturazione", r.id), {
        stato: nuovo, ...extra,
        ...(nuovo !== "fatturata" && r.stato === "fatturata" ? { fatturaRif: "", dataFattura: "" } : {}),
        statoCambiatoDaNome: chi, statoCambiatoIl: serverTimestamp(),
        storicoStati: arrayUnion({ da: r.stato || "", a: nuovo, chi, il: new Date().toISOString() }),
      });
      setInFattura(null);
      avvisaRichiedente({ richiesta: r, orgId: effectiveOrgId, mittenteUid: mioUid, nuovoStato: nuovo });
      sincronizzaFatturazioneCommessa(r.projectId, effectiveOrgId).catch(err => console.warn("Totali fatturazione non aggiornati:", err));
    } catch (err) { addToast(messaggioErrore(err, "cambiare lo stato"), "error"); }
  };

  const annullaMia = async (r) => {
    if (!await confirm(`Annullare la tua richiesta ${r.numero || ""}? La percentuale torna disponibile sulla commessa.`, { type: "danger" })) return;
    try {
      await annullaRichiestaFatturazione(r.id, currentUser?.displayName || currentUser?.email || "");
      addToast("Richiesta annullata", "info");
    } catch (err) { addToast(err?.message?.startsWith("La richiesta") ? err.message : messaggioErrore(err, "annullare la richiesta"), "error"); }
  };

  // PM che hanno fatto richieste (per chi vede tutto): una «pagina» per ciascuno
  const pmRichiedenti = (() => {
    const m = {};
    richieste.forEach(r => {
      const k = r.richiedenteUid || "";
      if (!m[k]) m[k] = { uid: k, nome: r.richiedenteNome || "Senza nome", inAttesa: 0 };
      if (r.stato === "da_fatturare" || r.stato === "in_lavorazione") m[k].inAttesa++;
    });
    return Object.values(m).sort((a, b) => a.nome.localeCompare(b.nome, "it"));
  })();

  // N. ordine dell'azienda (W440): quello attuale della commessa, altrimenti quello del momento della richiesta
  const numeroOrdineDi = (r) => nomiCommesse[r.projectId]?.numeroOrdine || r.numeroOrdine || "";
  const filtrate = richieste.filter(r => {
    if (vedeTutte && filtroPm && (r.richiedenteUid || "") !== filtroPm) return false;
    if (filtroStato && r.stato !== filtroStato) return false;
    if (filtroCommessa && r.projectId !== filtroCommessa) return false;
    if (!cerca) return true;
    const s = cerca.toLowerCase();
    return [numeroOrdineDi(r), r.numero, r.clienteNome, r.projectName, r.codiceCommessa, r.responsabileNome, r.richiedenteNome, r.fatturaRif].some(v => (v||"").toLowerCase().includes(s));
  });

  if (vedeTutte === null) return <div style={{ padding: 24, fontSize: 13, color: "#9ca3af" }}>Caricamento…</div>;

  const chip = (attivo) => ({ fontSize: 11.5, fontWeight: 700, padding: "4px 11px", borderRadius: 14, cursor: "pointer", border: `1px solid ${attivo ? "#7c3aed" : "#e5e7eb"}`, background: attivo ? "#7c3aed" : "#fff", color: attivo ? "#fff" : "#374151", fontFamily: "'Inter',sans-serif" });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "16px 24px", flexShrink: 0 }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
          <FileText size={17} color="#7c3aed" /> Richieste di fatturazione
        </h2>
        <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
          {vedeTutte
            ? "Le richieste dei PM all'amministrazione. Scegli un PM per vedere solo le sue; cambiando lo stato il PM riceve un avviso."
            : "Le tue richieste di fatturazione all'amministrazione: le vedi solo tu e chi gestisce la fatturazione. Puoi annullarle finché non sono prese in carico."}
        </p>
        {vedeTutte && pmRichiedenti.length > 0 && (
          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
            <button onClick={() => setFiltroPm("")} style={chip(!filtroPm)}>Tutti i PM</button>
            {pmRichiedenti.map(pm => (
              <button key={pm.uid || "nessuno"} onClick={() => setFiltroPm(pm.uid)} style={chip(filtroPm === pm.uid)}>
                {pm.nome}{pm.inAttesa ? ` · ${pm.inAttesa} in attesa` : ""}
              </button>
            ))}
          </div>
        )}
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          {[["richieste", `Richieste di fatturazione`], ["varianti", `Varianti${variantiDaFatturare.length ? ` (${variantiDaFatturare.length} da fatturare)` : ""}`]].map(([k, l]) => (
            <button key={k} onClick={() => { setVista(k); setFiltroStato(""); }}
              style={{ fontSize: 12, fontWeight: 700, padding: "5px 12px", borderRadius: 8, cursor: "pointer", border: `1px solid ${vista === k ? "#7c3aed" : "#e5e7eb"}`, background: vista === k ? "#f5f3ff" : "#fff", color: vista === k ? "#7c3aed" : "#6b7280" }}>{l}</button>
          ))}
        </div>
        {vista === "richieste" && <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 6 }}>{filtrate.length} richiest{filtrate.length===1?"a":"e"}{filtroCommessa?` per questa commessa`:` in elenco`} {richieste.length!==filtrate.length && `(su ${richieste.length} totali)`}</p>}
        {vista === "varianti" && <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 6 }}>Lavori extra accettati dal cliente, eseguiti e valorizzati nelle {vedeTutte ? "commesse" : "tue commesse"}: si fatturano a parte rispetto alle percentuali del contratto. Totale da fatturare € {new Intl.NumberFormat("it-IT",{minimumFractionDigits:2,maximumFractionDigits:2}).format(totaleDaFatturare)}{filtroCommessa ? " (commessa selezionata)" : ""}</p>}
        {filtroCommessa && (
          <button onClick={()=>setFiltroCommessa("")} style={{marginTop:6,fontSize:11,padding:"3px 10px",border:"1px solid #7c3aed",borderRadius:12,background:"#faf5ff",color:"#7c3aed",cursor:"pointer",fontWeight:600}}>
            × Rimuovi filtro commessa
          </button>
        )}
      </div>

      <div style={{ padding: "12px 24px", borderBottom: "1px solid #e5e7eb", background: "#fff", display: "flex", gap: 10 }}>
        <div style={{ position: "relative", maxWidth: 320 }}>
          <Search size={14} color="#9ca3af" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
          <input value={cerca} onChange={e => setCerca(e.target.value)} placeholder="Cerca per N. ordine, numero, cliente, commessa..."
            style={{ width: "100%", padding: "7px 10px 7px 32px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, fontFamily: "'Inter',sans-serif", outline: "none" }} />
        </div>
        <select value={filtroStato} onChange={e => setFiltroStato(e.target.value)}
          style={{ padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, fontFamily: "'Inter',sans-serif", outline: "none" }}>
          {vista === "richieste" ? <>
            <option value="">Tutti gli stati</option>
            {Object.entries(STATI).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
          </> : <>
            <option value="">Da fatturare</option>
            <option value="fatturata">Fatturate</option>
          </>}
        </select>
      </div>

      {vista === "varianti" && (
        <div style={{ flex: 1, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ position: "sticky", top: 0, background: "#f9fafb", zIndex: 1 }}>
                {["N. ordine","Eseguita il","Cliente","Commessa","Variante","Importo", filtroStato === "fatturata" ? "Fattura" : (vedeTutte ? "" : " ")].map(h=>(
                  <th key={h} style={{ textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "1px solid #e5e7eb", whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {variantiVisibili.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#9ca3af", fontSize: 13 }}>{filtroStato === "fatturata" ? "Nessuna variante fatturata." : "Nessuna variante da fatturare."}</td></tr>
              ) : variantiVisibili.map(v => {
                const c = nomiCommesse[v.projectId] || {};
                const cod = v.codiceCommessa || c.codiceCommessa;
                return (
                  <tr key={v.id} style={{ borderBottom: "1px solid #f3f4f6", verticalAlign: "top" }}>
                    <td style={{ padding: "9px 14px", fontSize: 12, fontWeight: 800, color: "#1e1b4b", whiteSpace: "nowrap" }}>{c.numeroOrdine || <span style={{ color: "#d1d5db", fontWeight: 400 }}>—</span>}</td>
                    <td style={{ padding: "9px 14px", fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>{v.data ? new Date(v.data+"T00:00:00").toLocaleDateString("it-IT") : "—"}</td>
                    <td style={{ padding: "9px 14px", fontSize: 12 }}>{v.clienteNome || c.clienteNome || "—"}</td>
                    <td onClick={()=>onNavigate && onNavigate({page:"project",projectId:v.projectId,initialTab:"extra"})} style={{ padding: "9px 14px", fontSize: 12, color:"#7c3aed", cursor:"pointer" }}>
                      {cod ? `[${cod}] ` : ""}{v.projectName || c.name || "—"}
                    </td>
                    <td style={{ padding: "9px 14px", fontSize: 12 }}>
                      <b>{v.titolo}</b>
                      {v.descrizione && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{v.descrizione}</div>}
                      {fatturando?.id === v.id && (
                        <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <input value={fatturando.fatturaRif} onChange={e=>setFatturando(f=>({...f,fatturaRif:e.target.value}))} placeholder="Numero fattura"
                            style={{ padding: "6px 8px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 12, width: 130 }} />
                          <input type="date" value={fatturando.dataFattura} onChange={e=>setFatturando(f=>({...f,dataFattura:e.target.value}))}
                            style={{ padding: "6px 8px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 12 }} />
                          <button onClick={confermaVarianteFatturata} disabled={confermando} style={{ opacity: confermando ? .6 : 1,  padding: "6px 12px", borderRadius: 7, border: "none", background: "#059669", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Conferma</button>
                          <button onClick={()=>setFatturando(null)} style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid #e5e7eb", background: "#fff", fontSize: 12, cursor: "pointer" }}>Annulla</button>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "9px 14px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>{formatEuro(importoVariante(v))}</td>
                    <td style={{ padding: "9px 14px", fontSize: 12, whiteSpace: "nowrap" }}>
                      {statoVariante(v).id === "fatturata"
                        ? <span style={{ color: "#059669" }}>{v.fatturaRif || "—"}{v.dataFattura ? ` del ${new Date(v.dataFattura+"T00:00:00").toLocaleDateString("it-IT")}` : ""}</span>
                        : vedeTutte && fatturando?.id !== v.id && <button onClick={()=>setFatturando({ id: v.id, fatturaRif: "", dataFattura: toLocalDateStr(new Date()) })}
                            style={{ fontSize: 11, fontWeight: 700, padding: "4px 9px", borderRadius: 6, border: "1px solid #059669", background: "#ecfdf5", color: "#059669", cursor: "pointer" }}>Segna fatturata</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {vista === "richieste" && <div style={{ flex: 1, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ position: "sticky", top: 0, background: "#f9fafb", zIndex: 1 }}>
              {["N. ordine","ID","Data","Cliente","Commessa","Richiesta da","Tipo","% Richiesta","% Totale","% Residua","Importo","Stato","Note"].map(h=>(
                <th key={h} style={{ textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em", borderBottom: "1px solid #e5e7eb", whiteSpace:"nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtrate.length === 0 ? (
              <tr><td colSpan={13} style={{ textAlign: "center", padding: 40, color: "#9ca3af", fontSize: 13 }}>{vedeTutte || richieste.length ? "Nessuna richiesta trovata." : "Non hai ancora fatto richieste di fatturazione. Si fanno dalla commessa: menu della commessa → Richiedi fatturazione."}</td></tr>
            ) : filtrate.map(r => {
              const st = STATI[r.stato] || STATI.da_fatturare;
              return (
                <tr key={r.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "9px 14px", fontSize: 12, fontWeight: 800, color: "#1e1b4b", whiteSpace: "nowrap" }}>{numeroOrdineDi(r) || <span style={{ color: "#d1d5db", fontWeight: 400 }}>—</span>}</td>
                  <td style={{ padding: "9px 14px", fontSize: 12, fontWeight: 700, color: "#7c3aed", fontFamily:"monospace" }}>{r.numero}</td>
                  <td style={{ padding: "9px 14px", fontSize: 12, color: "#6b7280" }}>{r.dataRichiesta ? new Date(r.dataRichiesta+"T00:00:00").toLocaleDateString("it-IT") : "—"}</td>
                  <td style={{ padding: "9px 14px", fontSize: 12 }}>{r.clienteNome || "—"}</td>
                  <td onClick={()=>onNavigate && onNavigate({page:"project",projectId:r.projectId})} style={{ padding: "9px 14px", fontSize: 12, color:"#7c3aed", cursor:"pointer" }}>
                    {r.codiceCommessa ? `[${r.codiceCommessa}] `:""}{r.projectName}
                  </td>
                  <td style={{ padding: "9px 14px", fontSize: 12 }}>{r.richiedenteNome || r.responsabileNome || "—"}</td>
                  <td style={{ padding: "9px 14px", fontSize: 12 }}>{r.tipo === "totale" ? "Totale" : "Parziale"}</td>
                  <td style={{ padding: "9px 14px", fontSize: 12 }}>{((r.percentuale||0)).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%</td>
                  <td style={{ padding: "9px 14px", fontSize: 12 }}>{((r.percentualeComplessiva||0)).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%</td>
                  <td style={{ padding: "9px 14px", fontSize: 12 }}>{((r.percentualeResidua||0)).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%</td>
                  <td style={{ padding: "9px 14px", fontSize: 12, fontWeight: r.importoRichiesto!=null?600:400, color: r.importoRichiesto!=null?"#111827":"#d97706" }}>{formatEuro(r.importoRichiesto)}</td>
                  <td style={{ padding: "9px 14px", whiteSpace: "nowrap" }}>
                    {vedeTutte ? (
                      <select value={r.stato} onChange={e=>cambiaStato(r, e.target.value)}
                        style={{ fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 8, border: "none", background: st.bg, color: st.col, cursor:"pointer" }}>
                        {Object.entries(STATI).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    ) : (
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 8, background: st.bg, color: st.col }}>{st.label}</span>
                    )}
                    {r.stato === "fatturata" && r.fatturaRif && <div style={{ fontSize: 10.5, color: "#059669", marginTop: 3 }}>Fattura {r.fatturaRif}{r.dataFattura ? ` del ${new Date(r.dataFattura+"T00:00:00").toLocaleDateString("it-IT")}` : ""}</div>}
                    {!vedeTutte && r.stato === "da_fatturare" && r.richiedenteUid === mioUid && (
                      <button onClick={() => annullaMia(r)} style={{ display: "block", marginTop: 4, fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 6, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", cursor: "pointer" }}>Annulla richiesta</button>
                    )}
                    {inFattura?.id === r.id && (
                      <div style={{ display: "flex", gap: 6, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
                        <input value={inFattura.fatturaRif} onChange={e=>setInFattura(f=>({...f,fatturaRif:e.target.value}))} placeholder="Numero fattura" autoFocus
                          style={{ padding: "5px 8px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 12, width: 120 }} />
                        <input type="date" value={inFattura.dataFattura} onChange={e=>setInFattura(f=>({...f,dataFattura:e.target.value}))}
                          style={{ padding: "5px 8px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 12 }} />
                        <button disabled={!inFattura.fatturaRif.trim()} onClick={() => cambiaStato(r, "fatturata", { fatturaRif: inFattura.fatturaRif.trim(), dataFattura: inFattura.dataFattura || "" })}
                          style={{ padding: "5px 10px", borderRadius: 7, border: "none", background: "#059669", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: inFattura.fatturaRif.trim() ? 1 : .5 }}>Conferma</button>
                        <button onClick={() => setInFattura(null)} style={{ padding: "5px 9px", borderRadius: 7, border: "1px solid #e5e7eb", background: "#fff", fontSize: 12, cursor: "pointer" }}>Annulla</button>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "9px 14px", fontSize: 12, color: "#9ca3af", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.note || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>}
    </div>
  );
}
